import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useRfcPrefill } from '../../../../shared/fiscal/useRfcPrefill';
import {
	buildRfcPrefillTracking,
	resolveRfcPrefillMissed,
	rfcToPrefill,
	type RfcPrefill,
	type RfcPrefilledSource,
	type RfcPrefillMissed,
	type RfcPrefillTracking,
} from '../../../../shared/fiscal/lib/rfcPrefill';
import {
	buildFullName,
	type ParsedFullName,
} from '../../../../shared/utils/parseFullName.util';
import { useOnboarding } from '../../../../features/onboarding/hooks/useOnboarding';
import type { RootState } from '../../../../redux/store';

export type UseCredentialsFormOptions = {
	/** Se llama cuando el campo de RFC aparece ya lleno, con la fuente. */
	onRfcPrefilled?: (source: RfcPrefilledSource) => void;
	onRfcPrefillMissed?: (missed: RfcPrefillMissed) => void;
};

/**
 * Sub-controller del formulario de credenciales SAT (RFC/contraseña + campos
 * e.firma). Extraído de LinkingFlowStep2 para que cualquier variante de UI lo
 * reuse sin re-implementar el prefill ni el puente con el RfcHelpDrawer.
 */
export const useCredentialsForm = ({
	onRfcPrefilled,
	onRfcPrefillMissed,
}: UseCredentialsFormOptions = {}) => {
	const onRfcPrefilledRef = useRef(onRfcPrefilled);
	onRfcPrefilledRef.current = onRfcPrefilled;
	const onRfcPrefillMissedRef = useRef(onRfcPrefillMissed);
	onRfcPrefillMissedRef.current = onRfcPrefillMissed;
	const [storedNames, setStoredNames] = useState<ParsedFullName | null>(null);
	const [storedCurp, setStoredCurp] = useState<string>('');

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		trigger,
		formState: { errors },
	} = useForm({
		defaultValues: {
			rfc: '',
			password: '',
			keyFile: undefined,
			cerFile: undefined,
			efirmaPassword: undefined,
		},
	});

	const { data: onboarding } = useOnboarding();
	const profileUser = useSelector((state: RootState) => state.profile.user);

	const rfcValue = (watch('rfc') || '').toUpperCase();

	const persistedNameParts = useMemo(() => {
		const ob = onboarding?.answers;
		const first_name =
			(ob?.first_name?.value as string | undefined) ?? profileUser?.first_name;
		const second_name =
			(ob?.second_name?.value as string | undefined) ?? profileUser?.middle_name;
		const apellido_paterno =
			(ob?.father_surname?.value as string | undefined) ??
			profileUser?.father_surname;
		const apellido_materno =
			(ob?.mother_surname?.value as string | undefined) ??
			profileUser?.mother_surname;
		return { first_name, second_name, apellido_paterno, apellido_materno };
	}, [onboarding, profileUser]);

	const initialDrawerCurp = useMemo(() => {
		if (storedCurp) return storedCurp;
		return (onboarding?.answers.curp?.value as string | undefined) ?? '';
	}, [storedCurp, onboarding]);

	const initialDrawerFullName = useMemo(() => {
		if (storedNames) return buildFullName(storedNames);
		return buildFullName(persistedNameParts);
	}, [storedNames, persistedNameParts]);

	const rfcPrefillState = useRfcPrefill();
	const rfcPrefill = rfcPrefillState.prefill;

	// Prellenado del RFC: solo si el campo está vacío, también cuando el RFC
	// llega tarde, y una sola vez. `setValue` es la API imperativa del form.
	const rfcPrefilledRef = useRef<RfcPrefill | null>(null);
	const rfcPrefillMissedRef = useRef(false);
	useEffect(() => {
		const rfc = rfcToPrefill(
			rfcValue,
			rfcPrefill,
			rfcPrefilledRef.current !== null,
		);
		if (rfc && rfcPrefill) {
			rfcPrefilledRef.current = rfcPrefill;
			setValue('rfc', rfc, { shouldValidate: true });
			onRfcPrefilledRef.current?.(rfcPrefill.eventSource);
			return;
		}
		if (rfcPrefilledRef.current || rfcPrefillMissedRef.current) return;
		const missed = resolveRfcPrefillMissed(rfcPrefillState, rfcValue !== '');
		if (!missed) return;
		rfcPrefillMissedRef.current = true;
		onRfcPrefillMissedRef.current?.(missed);
	}, [rfcPrefill, rfcPrefillState, rfcValue, setValue]);

	const handleRfcGenerated = ({
		rfc,
		curp,
		name,
	}: {
		rfc: string;
		curp: string;
		name: ParsedFullName;
	}) => {
		setValue('rfc', rfc, { shouldValidate: true });
		setStoredNames(name);
		setStoredCurp(curp);
	};

	type FormValues = Parameters<Parameters<typeof handleSubmit>[0]>[0];
	const handleSubmitWithPrefill = (
		onValid: (
			values: FormValues & { rfcPrefillTracking: RfcPrefillTracking },
		) => void,
	) =>
		handleSubmit((values) =>
			onValid({
				...values,
				rfcPrefillTracking: buildRfcPrefillTracking(
					rfcPrefilledRef.current,
					values.rfc,
				),
			}),
		);

	return {
		control,
		handleSubmit: handleSubmitWithPrefill,
		watch,
		setValue,
		trigger,
		errors,
		rfcPrefill,
		initialDrawerCurp,
		initialDrawerFullName,
		handleRfcGenerated,
	};
};
